import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditWallDialog } from "@/components/CreditWallDialog";
import {
  CREDITS_REQUIRED_EVENT,
  type CreditsRequiredDetail,
} from "@shared/auth-codes";

interface CreditWallState {
  action: string;
  cost: number;
}

interface CreditWallContextType {
  isOpen: boolean;
  openCreditWall: (state: CreditWallState) => void;
  closeCreditWall: () => void;
}

const CreditWallContext = createContext<CreditWallContextType>({
  isOpen: false,
  openCreditWall: () => {},
  closeCreditWall: () => {},
});

export function useCreditWall() {
  return useContext(CreditWallContext);
}

export function CreditWallProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [wallState, setWallState] = useState<CreditWallState>({ action: "", cost: 0 });

  const openCreditWall = useCallback((state: CreditWallState) => {
    setWallState(state);
    setIsOpen(true);
  }, []);

  const closeCreditWall = useCallback(() => {
    setIsOpen(false);
  }, []);

  // CLICK-credits-02: apiRequest dispatches CREDITS_REQUIRED_EVENT on 402 so any
  // mutation path opens the wall without per-call-site wiring.
  useEffect(() => {
    const onCreditsRequired = (event: Event) => {
      const detail = (event as CustomEvent<CreditsRequiredDetail>).detail ?? {};
      openCreditWall({
        action:
          typeof detail.action === "string" && detail.action.trim()
            ? detail.action
            : t("credits.wall.defaultAction", "Continue"),
        cost: typeof detail.cost === "number" && detail.cost > 0 ? detail.cost : 1,
      });
    };
    window.addEventListener(CREDITS_REQUIRED_EVENT, onCreditsRequired);
    return () => window.removeEventListener(CREDITS_REQUIRED_EVENT, onCreditsRequired);
  }, [openCreditWall, t]);

  const value = useMemo(
    () => ({ isOpen, openCreditWall, closeCreditWall }),
    [isOpen, openCreditWall, closeCreditWall],
  );

  return (
    <CreditWallContext.Provider value={value}>
      {children}
      <CreditWallDialog
        isOpen={isOpen}
        onClose={closeCreditWall}
        action={wallState.action}
        cost={wallState.cost}
      />
    </CreditWallContext.Provider>
  );
}
